/** hg-order's shapes, as this feature uses them: names for the generated types. */
import type { components } from "@/shared/api/order.schema";

type Schemas = components["schemas"];

export type Specialist = Schemas["SpecialistResponse"];
/** A profession of a list's row, in the request's language: its name, its level, how it stands. */
export type ProfessionSummary = Schemas["ProfessionSummary"];
export type SpecialistArea = Schemas["SpecialistAreaResponse"];
/**
 * One specialist in full: the list's row, and what staff decide on.
 *
 * The contract writes `professions` twice here (the row's summaries and the
 * page's own, by id), which no object can hold: the service's answer carries
 * the key twice and a browser keeps the later one, the professions by id.
 * So that is what this type says, and their names are read from the
 * reference data.
 */
export type SpecialistDetail = Omit<Specialist, "professions"> & {
  professions: SpecialistProfession[];
  service_areas: SpecialistArea[];
  credentials: Credential[];
  history: StatusEvent[];
};
/** How many specialists the list's filters match in each status. */
export type SpecialistCounts = Schemas["SpecialistStatusCountsResponse"];
/** A visit a specialist holds or held to its end. */
export type SpecialistVisit = Schemas["AdminSpecialistVisitResponse"];
export type VisitStatus = Schemas["VisitStatus"];
export type SpecialistStatus = Schemas["SpecialistStatus"];
export type Credential = Schemas["CredentialResponse"];
export type CredentialFile = Schemas["CredentialFileResponse"];
export type SpecialistProfession = Schemas["SpecialistProfessionResponse"];
export type StatusEvent = Schemas["StatusEventResponse"];
export type RatingSummary = Schemas["RatingSummaryResponse"];
export type StaffRating = Schemas["StaffRatingResponse"];
export type Gender = Schemas["Gender"];
export type ApprovalStatus = Schemas["ApprovalStatus"];

/** Every status a specialist can have, in the order a specialist lives through them. */
export const SPECIALIST_STATUSES = [
  "applicant",
  "in_review",
  "needs_changes",
  "approved",
  "suspended",
  "blocked",
  "rejected",
  "retired",
] as const satisfies readonly SpecialistStatus[];

/** Every status a visit can have, in the order a visit lives through them. */
export const VISIT_STATUSES = [
  "scheduled",
  "searching",
  "assigned",
  "en_route",
  "on_site",
  "completed",
  "cancelled",
  "unfulfilled",
] as const satisfies readonly VisitStatus[];
